// Imports: local files.
import ApiClient from '../../services/ApiClient';
import { showSnackbar, dispatchLoading, dispatchSuccess, dispatchError, getAxiosError } from '../../utils/redux';

// Name of property to be used in reducer data.
const productsName = 'products';
const productName = 'product';

// Action Types: Get All Products.
export const GET_PRODUCTS_START = 'GET_PRODUCTS_START';
export const GET_PRODUCTS_SUCCESS = 'GET_PRODUCTS_SUCCESS';
export const GET_PRODUCTS_FAILED = 'GET_PRODUCTS_FAILED';
export const GET_PRODUCTS_RESET = 'GET_PRODUCTS_RESET';

// Action Types: Get One Product.
export const GET_PRODUCT_START = 'GET_PRODUCT_START';
export const GET_PRODUCT_SUCCESS = 'GET_PRODUCT_SUCCESS';
export const GET_PRODUCT_FAILED = 'GET_PRODUCT_FAILED';
export const GET_PRODUCT_RESET = 'GET_PRODUCT_RESET';

// Action Types: Create Product.
export const CREATE_PRODUCT_START = 'CREATE_PRODUCT_START';
export const CREATE_PRODUCT_SUCCESS = 'CREATE_PRODUCT_SUCCESS';
export const CREATE_PRODUCT_FAILED = 'CREATE_PRODUCT_FAILED';
export const CREATE_PRODUCT_RESET = 'CREATE_PRODUCT_RESET';

// Action Types: Update Product.
export const UPDATE_PRODUCT_START = 'UPDATE_PRODUCT_START';
export const UPDATE_PRODUCT_SUCCESS = 'UPDATE_PRODUCT_SUCCESS';
export const UPDATE_PRODUCT_FAILED = 'UPDATE_PRODUCT_FAILED';
export const UPDATE_PRODUCT_RESET = 'UPDATE_PRODUCT_RESET';

// Action Types: Delete Product.
export const DELETE_PRODUCT_START = 'DELETE_PRODUCT_START';
export const DELETE_PRODUCT_SUCCESS = 'DELETE_PRODUCT_SUCCESS';
export const DELETE_PRODUCT_FAILED = 'DELETE_PRODUCT_FAILED';
export const DELETE_PRODUCT_RESET = 'DELETE_PRODUCT_RESET';

// Action Creators: Get All Products.
const getProductsStart = (payload) => ({ type: GET_PRODUCTS_START, payload });
const getProductsSuccess = (payload) => ({ type: GET_PRODUCTS_SUCCESS, payload });
const getProductsFailed = (payload) => ({ type: GET_PRODUCTS_FAILED, payload });
const getProductsReset = () => ({ type: GET_PRODUCTS_RESET });

// Action Creators: Get One Product.
const getProductStart = (payload) => ({ type: GET_PRODUCT_START, payload });
const getProductSuccess = (payload) => ({ type: GET_PRODUCT_SUCCESS, payload });
const getProductFailed = (payload) => ({ type: GET_PRODUCT_FAILED, payload });
const getProductReset = () => ({ type: GET_PRODUCT_RESET });

// Action Creators: Create Product.
const createProductStart = (payload) => ({ type: CREATE_PRODUCT_START, payload });
const createProductSuccess = (payload) => ({ type: CREATE_PRODUCT_SUCCESS, payload });
const createProductFailed = (payload) => ({ type: CREATE_PRODUCT_FAILED, payload });
const createProductReset = () => ({ type: CREATE_PRODUCT_RESET });

// Action Creators: Update Product.
const updateProductStart = (payload) => ({ type: UPDATE_PRODUCT_START, payload });
const updateProductSuccess = (payload) => ({ type: UPDATE_PRODUCT_SUCCESS, payload });
const updateProductFailed = (payload) => ({ type: UPDATE_PRODUCT_FAILED, payload });
const updateProductReset = () => ({ type: UPDATE_PRODUCT_RESET });

// Action Creators: Delete Product.
const deleteProductStart = (payload) => ({ type: DELETE_PRODUCT_START, payload });
const deleteProductSuccess = (payload) => ({ type: DELETE_PRODUCT_SUCCESS, payload });
const deleteProductFailed = (payload) => ({ type: DELETE_PRODUCT_FAILED, payload });
const deleteProductReset = () => ({ type: DELETE_PRODUCT_RESET });

// Actions: Get All Products.
export const getProducts = (payload, options = {}) => {
  const { page, limit, pagination, name } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, getProductsStart);

      const query = { page, limit, pagination, name };
      const result = await ApiClient.get('/products', { params: query });
      if (result.data?.success) {
        const { products } = result.data?.data;
        dispatchSuccess(dispatch, getProductsSuccess, products, productsName);
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, getProductsFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, getProductsFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Get All Products.
export const clearProducts = () => getProductsReset();

// Actions: Get One Product.
export const getProduct = (payload, options = {}) => {
  const { productId } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, getProductStart);

      const result = await ApiClient.get(`/products/${productId}`);
      if (result.data?.success) {
        const { product } = result.data?.data;
        dispatchSuccess(dispatch, getProductSuccess, product, productName);
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, getProductFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, getProductFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Get One Product.
export const clearProduct = () => getProductReset();

// Actions: Create Product.
export const createProduct = (payload, options = {}) => {
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, createProductStart);

      const fields = [
        'name',
        'shortDescription',
        'longDescription',
        'details',
        'informationLinks',
        'manufacturer',
        'tool',
        'photo',
        'video',
      ];
      const formData = new FormData();
      fields.forEach((field) => payload[field] && formData.append(field, payload[field]));

      const result = await ApiClient.post(`/products`, formData);
      if (result.data?.success) {
        const { product } = result.data?.data;
        dispatchSuccess(dispatch, createProductSuccess, product, productName);
        showSnackbar(dispatch, 'Product created successfully!', 'success');
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, createProductFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, createProductFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Created Product.
export const clearCreatedProduct = () => createProductReset();

// Actions: Update Product.
export const updateProduct = (payload, options = {}) => {
  const { productId } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, updateProductStart);

      const fields = [
        'name',
        'shortDescription',
        'longDescription',
        'details',
        'informationLinks',
        'manufacturer',
        'tool',
        'photo',
        'video',
        'toBeDeleted',
      ];
      const formData = new FormData();
      fields.forEach((field) => payload[field] && formData.append(field, payload[field]));

      const result = await ApiClient.put(`/products/${productId}`, formData);
      if (result.data?.success) {
        const { product } = result.data?.data;
        dispatchSuccess(dispatch, updateProductSuccess, product, productName);
        showSnackbar(dispatch, 'Product updated successfully!', 'success');
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, updateProductFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, updateProductFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Updated Product.
export const clearUpdatedProduct = () => updateProductReset();

// Actions: Delete Product.
export const deleteProduct = (payload, options = {}) => {
  const { productId } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, deleteProductStart);

      const result = await ApiClient.delete(`/products/${productId}`);
      if (result.data?.success) {
        const { product } = result.data?.data;
        dispatchSuccess(dispatch, deleteProductSuccess, product, productName);
        showSnackbar(dispatch, 'Product deleted successfully!', 'success');
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, deleteProductFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, deleteProductFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Deleted Product.
export const clearDeletedProduct = () => deleteProductReset();
